import { initializeApp } from 'firebase/app';
import { 
  getFirestore, 
  collection, 
  addDoc, 
  doc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  serverTimestamp,
  enableIndexedDbPersistence,
  enableMultiTabIndexedDbPersistence,
  CACHE_SIZE_UNLIMITED
} from 'firebase/firestore';
import { 
  getStorage as getFirebaseStorage, 
  ref, 
  uploadBytes, 
  getDownloadURL 
} from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { getPerformance } from 'firebase/performance';

// Firebase yapılandırması
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// Firebase app instance'ını hemen başlat
const app = initializeApp(firebaseConfig);

// Auth instance'ını hemen başlat
const auth = getAuth(app);

// Firestore instance'ını lazy loading ile başlat
let db = null;
let dbInitPromise = null;
let dbInitStarted = false;

const getDb = async () => {
  // Eğer db zaten başlatılmışsa, doğrudan döndür
  if (db) {
    return db;
  }
  
  // Eğer başlatma işlemi devam ediyorsa, mevcut promise'i döndür
  if (dbInitPromise) {
    return dbInitPromise;
  }
  
  // Admin sayfasında isek, hızlı başlatma yap
  const isAdminPage = window.location.pathname.startsWith('/admin');
  
  // Yeni bir başlatma işlemi başlat
  dbInitStarted = true;
  dbInitPromise = (async () => {
    try {
      console.time('Firestore initialization');
      
      // Firestore'u başlat
      const firestore = getFirestore(app);
      
      // Admin sayfasında isek, önbelleği devre dışı bırak
      if (!isAdminPage) {
        try {
          // Önbellek boyutunu sınırsız yap
          await enableIndexedDbPersistence(firestore, {
            cacheSizeBytes: CACHE_SIZE_UNLIMITED
          });
          console.log('Firestore persistence enabled');
        } catch (error) {
          if (error.code === 'failed-precondition') {
            console.warn('Persistence error: Multiple tabs open, using server data');
          } else if (error.code === 'unimplemented') {
            console.warn('Persistence error: Browser does not support IndexedDB');
          } else {
            console.warn('Persistence error:', error);
          }
        }
      } else {
        console.log('Admin page detected, persistence disabled for better performance');
      }
      
      // Başarılı olduğunda db değişkenini güncelle
      db = firestore;
      
      console.timeEnd('Firestore initialization');
      return db;
    } catch (error) {
      console.error('Firestore initialization error:', error);
      dbInitPromise = null;
      dbInitStarted = false;
      throw error;
    }
  })();
  
  return dbInitPromise;
};

// Storage instance'ını lazy loading ile başlat
let storage = null;
let storageInitPromise = null;
let storageInitStarted = false;

const getStorage = async () => {
  // Eğer storage zaten başlatılmışsa, doğrudan döndür
  if (storage) {
    return storage;
  }
  
  // Eğer başlatma işlemi devam ediyorsa, mevcut promise'i döndür
  if (storageInitPromise) {
    return storageInitPromise;
  }
  
  // Yeni bir başlatma işlemi başlat
  storageInitStarted = true;
  storageInitPromise = (async () => {
    try {
      console.time('Storage initialization');
      storage = getFirebaseStorage(app);
      console.timeEnd('Storage initialization');
      return storage;
    } catch (error) {
      console.error('Storage initialization error:', error);
      storageInitPromise = null;
      storageInitStarted = false;
      throw error;
    }
  })();
  
  return storageInitPromise;
};

// Performance monitoring'i lazy loading ile başlat
let perf = null;
const getPerf = async () => {
  if (!perf) {
    try {
      perf = getPerformance(app);
    } catch (error) {
      console.warn('Performance monitoring not available:', error);
      perf = {
        trace: () => ({
          start: () => {},
          stop: () => {},
          putAttribute: () => {}
        })
      };
    }
  }
  return perf;
};

// Collection referansları
const COLLECTIONS = {
  TALENT_TALKS: 'talent_talks',
  LOOKING_FOR_ROLE: 'looking_for_role',
  LOOKING_FOR_TALENT: 'looking_for_talent',
  JOB_APPLICATIONS: 'job_applications',
  JOBS: 'jobs'
};

// Dosya yükleme yardımcı fonksiyonu - memoize edildi
const uploadFileCache = new Map();
const uploadFile = async (file, path) => {
  const cacheKey = `${path}_${file.name}_${file.size}`;
  
  if (uploadFileCache.has(cacheKey)) {
    return uploadFileCache.get(cacheKey);
  }
  
  try {
    const storageInstance = await getStorage();
    const storageRef = ref(storageInstance, path);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    uploadFileCache.set(cacheKey, downloadURL);
    
    return downloadURL;
  } catch (error) {
    console.error('Dosya yükleme hatası:', error);
    throw new Error('Dosya yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
  }
};

// CV gönderimi için yardımcı fonksiyon
const submitCV = async (formData) => {
  try {
    let fileUrl = '';
    if (formData.file) {
      try {
        const fileExtension = formData.file.name.split('.').pop();
        const fileName = `${formData.formType}s/${Date.now()}.${fileExtension}`;
        fileUrl = await uploadFile(formData.file, fileName);
      } catch (error) {
        console.error('CV yükleme hatası:', error);
        throw new Error('CV yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
      }
    }

    const dbInstance = await getDb();
    const submissionData = {
      ...formData,
      fileUrl,
      createdAt: serverTimestamp(),
      status: 'pending',
      type: formData.formType,
      pageUrl: window.location.href,
      pagePath: window.location.pathname
    };

    delete submissionData.file;
    
    const collectionName = formData.formType === 'role' 
      ? COLLECTIONS.LOOKING_FOR_ROLE 
      : COLLECTIONS.LOOKING_FOR_TALENT;
    
    const docRef = await addDoc(collection(dbInstance, collectionName), submissionData);

    // Email gönderme isteği - asenkron olarak çalıştır
    fetch('https://us-central1-talent-finder-21f32.cloudfunctions.net/sendEmailNotification', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...submissionData,
        type: formData.formType === 'role' ? 'lookingForRole' : 'lookingForTalent'
      })
    }).catch(console.error);

    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Form gönderme hatası:', error);
    throw error;
  }
};

// İletişim mesajı gönderimi için yardımcı fonksiyon
const submitContactMessage = async (messageData) => {
  try {
    const dbInstance = await getDb();
    const docRef = await addDoc(collection(dbInstance, COLLECTIONS.LOOKING_FOR_ROLE), {
      ...messageData,
      createdAt: serverTimestamp(),
      status: 'unread',
      type: 'contact',
      pageUrl: window.location.href,
      pagePath: window.location.pathname
    });

    // Email gönderme isteği - asenkron olarak çalıştır
    fetch('https://us-central1-talent-finder-21f32.cloudfunctions.net/sendEmailNotification', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...messageData, type: 'contact' })
    }).catch(console.error);

    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error submitting message:', error);
    throw error;
  }
};

// İş başvurusu gönderimi için yardımcı fonksiyon
const submitJobApplication = async (applicationData) => {
  try {
    let resumeUrl = '';
    if (applicationData.resume) {
      const fileExtension = applicationData.resume.name.split('.').pop();
      const fileName = `job_applications/${Date.now()}.${fileExtension}`;
      resumeUrl = await uploadFile(applicationData.resume, fileName);
    }

    const dbInstance = await getDb();
    const submissionData = {
      ...applicationData,
      resumeUrl,
      createdAt: serverTimestamp(),
      status: 'pending',
      pageUrl: window.location.href,
      pagePath: window.location.pathname
    };

    delete submissionData.resume;
    
    const docRef = await addDoc(collection(dbInstance, COLLECTIONS.JOB_APPLICATIONS), submissionData);

    // Email gönderme isteği - asenkron olarak çalıştır
    fetch('https://us-central1-talent-finder-21f32.cloudfunctions.net/sendEmailNotification', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...submissionData, type: 'jobApplication' })
    }).catch(console.error);

    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error submitting job application:', error);
    throw error;
  }
};

// Önbellek temizleme fonksiyonu
const clearCache = () => {
  uploadFileCache.clear();
};

// Firebase başlatma fonksiyonu
const initializeFirebase = async () => {
  const dbInstance = await getDb();
  return { app, auth, db: dbInstance };
};

// Firebase User değişkenini doğrudan export et
const getCurrentUser = () => {
  return auth.currentUser;
};

export {
  auth,
  getAuth,
  getDb,
  getStorage,
  getPerf,
  COLLECTIONS,
  uploadFile,
  submitCV,
  submitContactMessage,
  submitJobApplication,
  clearCache,
  initializeFirebase,
  getCurrentUser
}; 
